/**
 * Pure grouping helper for the wizard's Actions step table. Takes the flat
 * ActionRow[] list and produces a nested bank → lift structure that the
 * StepActions component renders as one section per bank.
 *
 * Entries with no liftName (bank-level controls, e.g. EnergizeUpHallCall)
 * land under a synthetic "Bank-Level" lift group that sorts FIRST inside
 * the bank so operators see the bank-wide commands before per-car ones.
 *
 * Bank groups sort alphabetically by bank name. Lift groups within a bank
 * sort alphabetically by lift name, with the Bank-Level group pinned to
 * the top.
 *
 * Exported as a pure function (no React) so the test file can verify
 * grouping behaviour without rendering the component.
 */
import { ActionScope } from "../../../flatbuffers/generated/archive/action-scope";

export interface ActionRowLike {
  key: string;
  id: string;
  label: string;
  messageType: string;
  bankId?: string;
  bankName: string;
  liftId?: string;
  liftName: string;
  targetPosition: number;
  buttonType: string;
  timer?: number;
  toggleMessageType?: string;
  toggleLabel?: string;
  /** Archive.ActionScope, as the server derived it. Absent on older rows. */
  actionScope?: number;
}

export const BANK_LEVEL_LIFT_KEY = "__BANK_LEVEL__";
export const BANK_LEVEL_LIFT_LABEL = "Bank-Level";
export const LIFT_LEVEL_LABEL = "Lift-Level";

/**
 * Shown for a UNIT-scoped action that is not pinned to one car.
 *
 * Distinct from BANK_LEVEL_LIFT_LABEL on purpose: bank-wide means the vendor
 * encodes the command once for the whole group, while this means it addresses a
 * single car and monitoring offers it on each one. Collapsing them — which the
 * Lift cell did until the column started reading `actionScope` — told an
 * operator that a per-car command could not be aimed at a car.
 */
export const ALL_CARS_LIFT_LABEL = "All Cars";

/**
 * A UNIT-scoped action that names no particular car.
 *
 * Distinct from bank-level, and the difference is not cosmetic. A bank-level
 * command addresses the whole group through one register; a unit-scoped one
 * addresses each car through its own, and a row like this simply applies to all
 * of them. Calling the second "Bank-Level" told an operator their per-car
 * command was a group command — and the monitoring dashboard already draws the
 * distinction correctly, putting these on the LIFT tab, so the two screens
 * disagreed about the same row.
 */
export const UNIT_ALL_LIFTS_KEY = "__UNIT_ALL_LIFTS__";
export const UNIT_ALL_LIFTS_LABEL = "All Lifts (Unit-Level)";

/**
 * Which group a row belongs in.
 *
 * Scope wins wherever the server gave one, because it describes how the vendor
 * ENCODES the command. The lift name is a display value, and an absent one has
 * never meant "bank level" — just as often it means the name lookup missed.
 * UNKNOWN and absent fall back to the original name-based rule, so rows from a
 * server predating the field group exactly as they always did.
 */
function liftKeyOf(row: ActionRowLike): string {
  const liftName = row.liftName || "";

  if (row.actionScope === ActionScope.BANK) return BANK_LEVEL_LIFT_KEY;

  if (row.actionScope === ActionScope.UNIT && !row.liftId && liftName === "") {
    return UNIT_ALL_LIFTS_KEY;
  }

  return liftName === "" ? BANK_LEVEL_LIFT_KEY : liftName;
}

/**
 * What the Lift cell says about a row, as one value.
 *
 * Derived once from the row's lift name and `actionScope` so the tag, its
 * tooltip and the column help all come from the same place. A fourth scope
 * added here fails to compile until LIFT_SCOPE_PRESENTATION handles it.
 *
 * - LIFT: pinned to one car.
 * - ALL_CARS: UNIT-scoped and not pinned; addressed on each car's own register.
 * - BANK: the server said the command is bank-wide.
 * - BANK_UNDETERMINED: the server could not tell, so the bank-wide default is
 *   shown rather than inventing a scope for a row that says it has none.
 */
export type LiftScope =
  | { kind: "LIFT"; liftName: string }
  | { kind: "ALL_CARS" }
  | { kind: "BANK" }
  | { kind: "BANK_UNDETERMINED" };

export function deriveLiftScope(row: Pick<ActionRowLike, "liftName" | "actionScope">): LiftScope {
  if (row.liftName) return { kind: "LIFT", liftName: row.liftName };
  if (row.actionScope === ActionScope.UNIT) return { kind: "ALL_CARS" };
  if (row.actionScope === ActionScope.BANK) return { kind: "BANK" };
  return { kind: "BANK_UNDETERMINED" };
}

export interface LiftScopePresentation {
  label: string;
  color: string;
  tooltip: string;
}

/**
 * How a scope renders. An exhaustive switch: a LiftScope kind added above
 * leaves `scope` un-narrowed to `never` here and fails to compile until it is
 * handled.
 */
export function presentLiftScope(scope: LiftScope): LiftScopePresentation {
  switch (scope.kind) {
    case "LIFT":
      return {
        label: `${LIFT_LEVEL_LABEL}: ${scope.liftName}`,
        color: "green",
        tooltip: `This command is pinned to the lift "${scope.liftName}" and is offered only on that car.`,
      };
    case "ALL_CARS":
      return {
        label: ALL_CARS_LIFT_LABEL,
        color: "blue",
        tooltip:
          "This command addresses one car. It is not pinned to a specific lift, so monitoring offers it on every car in the bank.",
      };
    case "BANK":
      return {
        label: BANK_LEVEL_LIFT_LABEL,
        color: "purple",
        tooltip:
          "This command is bank-wide — the vendor encodes it once for the whole group, so there is no single car to send it to.",
      };
    case "BANK_UNDETERMINED":
      return {
        label: BANK_LEVEL_LIFT_LABEL,
        color: "purple",
        tooltip:
          "The server could not determine this command's scope from the adapter key, so the bank-wide default is shown.",
      };
    default: {
      const unhandled: never = scope;
      return unhandled;
    }
  }
}

/** The Lift column's help text, built from the same labels the cells show. */
export const LIFT_COLUMN_HELP =
  `Which car this action addresses. ${LIFT_LEVEL_LABEL} is pinned to one car. ` +
  `${ALL_CARS_LIFT_LABEL} addresses each car through its own register. ` +
  `${BANK_LEVEL_LIFT_LABEL} is bank-wide, with no single car to send to.`;

export interface LiftGroup<TRow extends ActionRowLike = ActionRowLike> {
  liftKey: string;
  liftName: string;
  rows: TRow[];
}

export interface BankGroup<TRow extends ActionRowLike = ActionRowLike> {
  bankName: string;
  liftGroups: LiftGroup<TRow>[];
}

/**
 * Group action rows into bank → lift buckets and sort deterministically.
 *
 * Sort rules:
 *  - Bank groups: alphabetical by bankName, then by row.key as tiebreaker
 *  - Lift groups: Bank-Level first, then alphabetical by liftName
 *  - Rows within a lift group: alphabetical by label, then by key for stable ordering
 */
export function groupActionsByBankAndLift<TRow extends ActionRowLike>(
  rows: TRow[],
): BankGroup<TRow>[] {
  const bankMap = new Map<string, Map<string, TRow[]>>();

  rows.forEach((row) => {
    const bankName = row.bankName || "";
    const liftKey = liftKeyOf(row);

    let liftMap = bankMap.get(bankName);
    if (!liftMap) {
      liftMap = new Map<string, TRow[]>();
      bankMap.set(bankName, liftMap);
    }
    const bucket = liftMap.get(liftKey);
    if (bucket) {
      bucket.push(row);
    } else {
      liftMap.set(liftKey, [row]);
    }
  });

  const bankNames = Array.from(bankMap.keys()).sort((a, b) => a.localeCompare(b));

  return bankNames.map((bankName) => {
    const liftMap = bankMap.get(bankName) as Map<string, TRow[]>;
    const liftKeys = Array.from(liftMap.keys()).sort(compareLiftKeys);
    const liftGroups: LiftGroup<TRow>[] = liftKeys.map((liftKey) => {
      const rawRows = liftMap.get(liftKey) as TRow[];
      const sortedRows = [...rawRows].sort(compareActionRowsInLiftGroup);
      const liftName = LIFT_GROUP_LABELS[liftKey] ?? liftKey;
      return { liftKey, liftName, rows: sortedRows };
    });
    return { bankName, liftGroups };
  });
}

/**
 * Display names for the two synthetic groups, keyed by their synthetic key.
 *
 * A lookup rather than a chain of comparisons so adding a third scope cannot
 * silently fall through to rendering its own sentinel key at an operator.
 */
const LIFT_GROUP_LABELS: Record<string, string> = {
  [BANK_LEVEL_LIFT_KEY]: BANK_LEVEL_LIFT_LABEL,
  [UNIT_ALL_LIFTS_KEY]: UNIT_ALL_LIFTS_LABEL,
};

/**
 * Group order within a bank: the widest scope first, then named cars.
 *
 * Bank-level commands address the whole group, unit-level-all-lifts address
 * every car individually, and a named car addresses one. Reading top to bottom
 * therefore narrows, which is the order an operator commissioning a bank works
 * in.
 */
const LIFT_GROUP_RANK: Record<string, number> = {
  [BANK_LEVEL_LIFT_KEY]: 0,
  [UNIT_ALL_LIFTS_KEY]: 1,
};

function compareLiftKeys(a: string, b: string): number {
  const rankA = LIFT_GROUP_RANK[a] ?? 2;
  const rankB = LIFT_GROUP_RANK[b] ?? 2;
  if (rankA !== rankB) return rankA - rankB;
  return a.localeCompare(b);
}

/**
 * The order rows take INSIDE one lift group: by label, then by key so two rows
 * sharing a label keep a stable order across refetches.
 *
 * Exported, and typed on the two fields it reads rather than on ActionRowLike,
 * because the monitoring control panel's Actions list renders the same
 * `/action-banks/by-bank` rows from its own row interface and has to list them
 * in the same order. That list exists to agree with this table; a comparator
 * written out twice is precisely where the two would drift.
 */
export function compareActionRowsInLiftGroup(
  a: { label: string; key: string },
  b: { label: string; key: string },
): number {
  const labelDelta = a.label.localeCompare(b.label);
  if (labelDelta !== 0) return labelDelta;
  return a.key.localeCompare(b.key);
}

/**
 * Pure helper used by StepActions.handleDeleteRow to prune a deleted
 * action's id out of the wizard's tracked actionCatalogIds. Exposed and
 * tested separately so the deletion contract is verifiable without
 * exercising the AntD Popconfirm + RTK mutation pipeline.
 */
export function pruneActionCatalogId(prevIds: string[], deletedId: string): string[] {
  return prevIds.filter((id) => id !== deletedId);
}
